import { useEffect } from "react";

// ---------------------------------------------------------------------------
// voiceCallStyles — the stylesheet behind the live-voice call.
//
// Injected once as a <style> tag rather than written as Tailwind classes so the
// overlay keeps working if it is ever portalled outside #root (Tailwind v4
// scopes to #root) and never inherits the app theme. Everything here is
// prefixed `xv-` so it cannot collide with anything else in the app.
//
// The look: Apple-grade dark glass — deep Xale green ground, drifting aurora,
// film grain, frosted controls, and Apple's own easing
// cubic-bezier(0.32, 0.72, 0, 1) on every transition.
// ---------------------------------------------------------------------------

const STYLE_ID = "xale-voice-call-styles";

export const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

export const VOICE_CALL_CSS = `
.xv-root {
  --xv-ink: #eaf5f1;
  --xv-dim: rgba(234, 245, 241, 0.62);
  --xv-faint: rgba(234, 245, 241, 0.38);
  --xv-line: rgba(255, 255, 255, 0.14);
  --xv-glass: rgba(255, 255, 255, 0.07);
  --xv-glass-hi: rgba(255, 255, 255, 0.13);
  --xv-green: #6fb99c;
  --xv-deep: #051912;
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--xv-ink);
  background: #03110c;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text",
    "Helvetica Neue", "Segoe UI", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizeLegibility;
  animation: xv-enter 560ms ${EASE} both;
}
@keyframes xv-enter {
  from { opacity: 0; transform: scale(1.035); filter: blur(14px); }
  to   { opacity: 1; transform: scale(1);     filter: blur(0); }
}

/* ---- Ambient ground ------------------------------------------------- */
.xv-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(70% 54% at 50% 44%, #17583f 0%, #0d3b2a 36%, #071f16 64%, #03110c 84%, #010806 100%);
}
.xv-aurora { position: absolute; inset: -25%; pointer-events: none; opacity: 0.5; }
.xv-aurora span {
  position: absolute;
  display: block;
  border-radius: 50%;
  mix-blend-mode: screen;
  filter: blur(80px);
  will-change: transform;
}
.xv-aurora span:nth-child(1) {
  width: 46vw; height: 46vw; left: 6%; top: 8%;
  background: radial-gradient(circle, rgba(70, 165, 128, 0.50) 0%, rgba(70, 165, 128, 0.26) 28%, rgba(70, 165, 128, 0.09) 54%, rgba(70, 165, 128, 0.02) 78%, rgba(70, 165, 128, 0) 100%);
  animation: xv-drift-a 34s ease-in-out infinite;
}
.xv-aurora span:nth-child(2) {
  width: 52vw; height: 52vw; right: 2%; top: 22%;
  background: radial-gradient(circle, rgba(59, 216, 255, 0.22) 0%, rgba(59, 216, 255, 0.11) 30%, rgba(59, 216, 255, 0.04) 56%, rgba(59, 216, 255, 0.01) 78%, rgba(59, 216, 255, 0) 100%);
  animation: xv-drift-b 46s ease-in-out infinite;
}
.xv-aurora span:nth-child(3) {
  width: 60vw; height: 60vw; left: 22%; bottom: -18%;
  background: radial-gradient(circle, rgba(33, 135, 96, 0.46) 0%, rgba(33, 135, 96, 0.24) 30%, rgba(33, 135, 96, 0.08) 56%, rgba(33, 135, 96, 0.02) 80%, rgba(33, 135, 96, 0) 100%);
  animation: xv-drift-c 40s ease-in-out infinite;
}
@keyframes xv-drift-a {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50%      { transform: translate3d(9vw, 6vh, 0) scale(1.16); }
}
@keyframes xv-drift-b {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1.08); }
  50%      { transform: translate3d(-11vw, 8vh, 0) scale(0.92); }
}
@keyframes xv-drift-c {
  0%, 100% { transform: translate3d(0, 0, 0) scale(0.95); }
  50%      { transform: translate3d(7vw, -7vh, 0) scale(1.2); }
}
/* Film grain — the single cheapest trick for a premium surface. */
.xv-grain {
  position: absolute;
  inset: -50%;
  pointer-events: none;
  opacity: 0.07;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/><feColorMatrix type='saturate' values='0'/></filter><rect width='160' height='160' filter='url(%23n)' opacity='0.9'/></svg>");
  animation: xv-grain-shift 6s steps(6) infinite;
}
@keyframes xv-grain-shift {
  0%   { transform: translate3d(0, 0, 0); }
  20%  { transform: translate3d(-3%, 2%, 0); }
  40%  { transform: translate3d(2%, -3%, 0); }
  60%  { transform: translate3d(-2%, -2%, 0); }
  80%  { transform: translate3d(3%, 1%, 0); }
  100% { transform: translate3d(0, 0, 0); }
}
.xv-vignette {
  position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(120% 90% at 50% 45%, transparent 48%, rgba(0, 0, 0, 0.55) 100%);
}
/* Apple-Intelligence edge light — opacity is driven from the audio loop. */
.xv-edge {
  position: absolute; inset: 0; pointer-events: none; opacity: 0;
  box-shadow:
    inset 0 0 70px 4px rgba(111, 185, 156, 0.34),
    inset 0 0 190px 56px rgba(33, 135, 96, 0.20);
  animation: xv-edge-drift 11s ease-in-out infinite;
}
@keyframes xv-edge-drift {
  0%, 100% { filter: hue-rotate(0deg); }
  50%      { filter: hue-rotate(-16deg); }
}

/* ---- Chrome --------------------------------------------------------- */
.xv-layer { position: relative; z-index: 1; display: flex; flex-direction: column; height: 100%; min-height: 0; }
.xv-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; padding: 18px 22px;
  animation: xv-fade-down 640ms ${EASE} 120ms both;
}
@keyframes xv-fade-down { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: none; } }
.xv-brand { display: flex; align-items: center; gap: 11px; min-width: 0; }
.xv-mark {
  width: 34px; height: 34px; border-radius: 11px; display: grid; place-items: center;
  background: linear-gradient(155deg, #f4fbf8 0%, #cfe9de 55%, #a9d6c4 100%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.8);
  overflow: hidden; flex: none;
}
.xv-mark img { width: 24px; height: 24px; object-fit: contain; display: block; }
.xv-title { font-size: 15px; font-weight: 600; letter-spacing: -0.012em; line-height: 1.2; }
.xv-sub {
  display: flex; align-items: center; gap: 6px;
  font-size: 11.5px; color: var(--xv-dim); letter-spacing: 0.012em; margin-top: 2px;
}
.xv-livedot {
  width: 6px; height: 6px; border-radius: 50%; background: #6fb99c; flex: none;
  box-shadow: 0 0 0 0 rgba(111, 185, 156, 0.55);
  animation: xv-ping 2.2s ${EASE} infinite;
}
.xv-livedot.is-off { background: #9ba3a0; animation: none; box-shadow: none; }
.xv-livedot.is-bad { background: #e82c4e; animation-duration: 1.2s; box-shadow: 0 0 0 0 rgba(232, 44, 78, 0.5); }
@keyframes xv-ping {
  0%   { box-shadow: 0 0 0 0 rgba(111, 185, 156, 0.5); }
  70%  { box-shadow: 0 0 0 7px rgba(111, 185, 156, 0); }
  100% { box-shadow: 0 0 0 0 rgba(111, 185, 156, 0); }
}
.xv-tools { display: flex; align-items: center; gap: 8px; }

/* Frosted capsule */
.xv-pill {
  display: inline-flex; align-items: center; gap: 6px;
  height: 32px; padding: 0 13px; border-radius: 999px;
  font: inherit; font-size: 12.5px; font-weight: 500; letter-spacing: 0.006em;
  color: var(--xv-ink); cursor: pointer;
  background: var(--xv-glass);
  border: 1px solid var(--xv-line);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  backdrop-filter: blur(22px) saturate(160%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 4px 14px rgba(0, 0, 0, 0.22);
  transition: background 200ms ${EASE}, transform 160ms ${EASE}, border-color 200ms ${EASE}, opacity 200ms;
}
.xv-pill:hover { background: var(--xv-glass-hi); }
.xv-pill:active { transform: scale(0.96); }
.xv-pill.is-on { background: rgba(111, 185, 156, 0.22); border-color: rgba(111, 185, 156, 0.5); }
.xv-pill.is-icon { width: 32px; padding: 0; justify-content: center; }
.xv-pill.is-primary {
  background: linear-gradient(180deg, #7fc4a8 0%, #46a580 100%);
  color: #04160f; border-color: rgba(255, 255, 255, 0.28); font-weight: 600;
  box-shadow: 0 10px 26px rgba(70, 165, 128, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.45);
}
.xv-pill.is-primary:hover { filter: brightness(1.06); }
.xv-count {
  min-width: 17px; height: 17px; padding: 0 5px; border-radius: 999px;
  display: inline-grid; place-items: center; font-size: 10.5px; font-weight: 600;
  background: rgba(255, 255, 255, 0.16);
}

/* ---- Stage ---------------------------------------------------------- */
.xv-stage {
  flex: 1; min-height: 0; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 16px; padding: 4px 24px 8px;
  overflow: hidden;
}
.xv-orbwrap, .xv-caps, .xv-stage > div { flex: none; }
.xv-orbwrap { position: relative; animation: xv-orb-in 900ms ${EASE} 80ms both; }
@keyframes xv-orb-in {
  from { opacity: 0; transform: scale(0.62); filter: blur(10px); }
  to   { opacity: 1; transform: scale(1);    filter: blur(0); }
}
.xv-status {
  display: inline-flex; align-items: center; gap: 9px;
  padding: 7px 15px 7px 12px; border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.09);
  -webkit-backdrop-filter: blur(18px); backdrop-filter: blur(18px);
  font-size: 13.5px; letter-spacing: 0.004em; color: rgba(234, 245, 241, 0.88);
  animation: xv-fade-in 420ms ${EASE} both;
}
@keyframes xv-fade-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.xv-eq { display: inline-flex; align-items: flex-end; gap: 2.5px; height: 13px; }
.xv-eq i {
  width: 2.5px; border-radius: 2px; background: currentColor; opacity: 0.85;
  animation: xv-eq 1s ease-in-out infinite;
}
.xv-eq i:nth-child(1) { height: 5px;  animation-delay: 0ms; }
.xv-eq i:nth-child(2) { height: 11px; animation-delay: 140ms; }
.xv-eq i:nth-child(3) { height: 7px;  animation-delay: 280ms; }
.xv-eq i:nth-child(4) { height: 12px; animation-delay: 90ms; }
@keyframes xv-eq { 0%, 100% { transform: scaleY(0.42); } 50% { transform: scaleY(1); } }
.xv-thinking { display: inline-flex; gap: 4px; }
.xv-thinking i {
  width: 5px; height: 5px; border-radius: 50%; background: currentColor;
  animation: xv-bounce 1.15s ease-in-out infinite;
}
.xv-thinking i:nth-child(2) { animation-delay: 150ms; }
.xv-thinking i:nth-child(3) { animation-delay: 300ms; }
@keyframes xv-bounce { 0%, 80%, 100% { transform: translateY(0); opacity: 0.45; } 40% { transform: translateY(-4px); opacity: 1; } }
.xv-tool {
  font-size: 12px; color: var(--xv-faint); letter-spacing: 0.01em;
  animation: xv-fade-in 400ms ${EASE} both;
}

/* ---- Captions ------------------------------------------------------- */
.xv-caps {
  width: 100%; max-width: 680px; min-height: 92px;
  display: flex; flex-direction: column; align-items: center; gap: 10px; text-align: center;
}
.xv-cap-you {
  font-size: 14.5px; line-height: 1.45; color: var(--xv-faint);
  letter-spacing: 0.004em; max-width: 90%;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  animation: xv-fade-in 320ms ${EASE} both;
}
.xv-cap-ai {
  font-size: 21px; line-height: 1.45; font-weight: 450; letter-spacing: -0.014em;
  color: #f2fbf7; max-width: 100%;
  text-shadow: 0 2px 26px rgba(111, 185, 156, 0.35);
  display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden;
}
.xv-word { display: inline-block; animation: xv-word-in 420ms ${EASE} both; }
@keyframes xv-word-in {
  from { opacity: 0; transform: translateY(0.32em); filter: blur(5px); }
  to   { opacity: 1; transform: none; filter: blur(0); }
}
.xv-hint { font-size: 12.5px; color: var(--xv-faint); letter-spacing: 0.01em; }

/* ---- Controls ------------------------------------------------------- */
.xv-dock {
  display: flex; align-items: center; justify-content: center; gap: 18px;
  padding: 12px 24px 34px;
  animation: xv-fade-up 640ms ${EASE} 200ms both;
}
@keyframes xv-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: none; } }
.xv-ctl {
  position: relative; width: 58px; height: 58px; border-radius: 50%;
  display: inline-grid; place-items: center; cursor: pointer; color: var(--xv-ink);
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(255, 255, 255, 0.16);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  backdrop-filter: blur(24px) saturate(160%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.22), 0 10px 26px rgba(0, 0, 0, 0.32);
  transition: transform 180ms ${EASE}, background 200ms ${EASE}, color 200ms ${EASE}, box-shadow 200ms ${EASE}, opacity 200ms;
}
.xv-ctl:hover { background: rgba(255, 255, 255, 0.17); transform: translateY(-2px); }
.xv-ctl:active { transform: scale(0.93); }
.xv-ctl:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
.xv-ctl.is-on {
  background: rgba(234, 245, 241, 0.94); color: #04160f; border-color: transparent;
  box-shadow: 0 10px 30px rgba(234, 245, 241, 0.28);
}
.xv-ctl.is-danger {
  width: 66px; height: 66px;
  background: linear-gradient(180deg, #f0455f 0%, #d2233e 100%);
  color: #fff; border-color: rgba(255, 255, 255, 0.24);
  box-shadow: 0 14px 36px rgba(218, 42, 70, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.35);
}
.xv-ctl.is-danger:hover { filter: brightness(1.07); }
.xv-ctl-label {
  position: absolute; top: calc(100% + 8px); left: 50%; transform: translateX(-50%);
  font-size: 11px; color: var(--xv-faint); white-space: nowrap; letter-spacing: 0.01em;
}

/* ---- Sheets (transcript, keyboard, recording) ------------------------ */
.xv-sheet-wrap { position: absolute; inset: 0; z-index: 3; display: flex; flex-direction: column; justify-content: flex-end; }
.xv-scrim {
  position: absolute; inset: 0; background: rgba(2, 10, 7, 0.55);
  -webkit-backdrop-filter: blur(3px); backdrop-filter: blur(3px);
  animation: xv-fade-in 260ms ease both; border: 0; cursor: default;
}
.xv-sheet {
  position: relative; width: 100%; max-width: 760px; margin: 0 auto;
  max-height: 68%; display: flex; flex-direction: column;
  border-radius: 26px 26px 0 0;
  background: linear-gradient(180deg, rgba(14, 42, 32, 0.92) 0%, rgba(5, 25, 18, 0.96) 100%);
  border: 1px solid rgba(255, 255, 255, 0.12); border-bottom: 0;
  -webkit-backdrop-filter: blur(36px) saturate(150%); backdrop-filter: blur(36px) saturate(150%);
  box-shadow: 0 -28px 70px rgba(0, 0, 0, 0.5);
  animation: xv-sheet-up 480ms ${EASE} both;
}
@keyframes xv-sheet-up { from { transform: translateY(102%); } to { transform: none; } }
.xv-grip { width: 38px; height: 4px; border-radius: 999px; background: rgba(255, 255, 255, 0.22); margin: 10px auto 2px; }
.xv-sheet-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 20px 12px; font-size: 13px; color: var(--xv-dim); letter-spacing: 0.02em;
  text-transform: uppercase; font-weight: 600;
}
.xv-sheet-body { overflow-y: auto; padding: 0 20px 24px; display: flex; flex-direction: column; gap: 12px; }
.xv-line { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; animation: xv-fade-in 320ms ${EASE} both; }
.xv-line.is-you { align-items: flex-end; }
.xv-role { font-size: 10.5px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--xv-faint); }
.xv-bubble {
  max-width: 84%; width: fit-content; padding: 10px 14px; border-radius: 16px; font-size: 14.5px; line-height: 1.5;
  background: rgba(255, 255, 255, 0.07); border: 1px solid rgba(255, 255, 255, 0.10);
}
.xv-line.is-ai .xv-bubble {
  background: linear-gradient(180deg, rgba(111, 185, 156, 0.18), rgba(111, 185, 156, 0.08));
  border-color: rgba(111, 185, 156, 0.30);
}
.xv-line.is-cut .xv-bubble { opacity: 0.55; }

/* ---- Typed input ---------------------------------------------------- */
.xv-type { display: flex; gap: 10px; width: 100%; max-width: 680px; margin: 0 auto; padding: 0 24px 12px; }
.xv-input {
  flex: 1; height: 44px; padding: 0 16px; border-radius: 999px; font: inherit; font-size: 14.5px;
  color: var(--xv-ink); background: rgba(255, 255, 255, 0.08);
  border: 1px solid var(--xv-line); outline: none;
  -webkit-backdrop-filter: blur(22px); backdrop-filter: blur(22px);
  transition: border-color 200ms ${EASE}, background 200ms ${EASE};
}
.xv-input::placeholder { color: var(--xv-faint); }
.xv-input:focus { border-color: rgba(111, 185, 156, 0.55); background: rgba(255, 255, 255, 0.12); }

/* ---- Recording card ------------------------------------------------- */
.xv-rec {
  width: 100%; max-width: 680px; margin: 0 auto 10px; padding: 14px 16px;
  border-radius: 20px; display: flex; flex-direction: column; gap: 11px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.10), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(111, 185, 156, 0.34);
  -webkit-backdrop-filter: blur(30px) saturate(160%); backdrop-filter: blur(30px) saturate(160%);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255, 255, 255, 0.18);
  animation: xv-rec-in 460ms ${EASE} both;
}
@keyframes xv-rec-in { from { opacity: 0; transform: translateY(20px) scale(0.98); } to { opacity: 1; transform: none; } }
.xv-rec-top { display: flex; align-items: center; gap: 12px; }
.xv-play {
  width: 42px; height: 42px; flex: none; border-radius: 50%; display: grid; place-items: center;
  cursor: pointer; color: #04160f; border: 1px solid rgba(255, 255, 255, 0.3);
  background: linear-gradient(180deg, #8fd0b4 0%, #46a580 100%);
  box-shadow: 0 8px 20px rgba(70, 165, 128, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.45);
  transition: transform 160ms ${EASE}, filter 160ms ${EASE};
}
.xv-play:hover { filter: brightness(1.07); }
.xv-play:active { transform: scale(0.93); }
.xv-rec-meta { min-width: 0; flex: 1; }
.xv-rec-title {
  font-size: 13.5px; font-weight: 550; letter-spacing: -0.006em;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.xv-rec-sub { font-size: 11.5px; color: var(--xv-faint); margin-top: 2px; }
.xv-wave { display: flex; align-items: center; gap: 2px; height: 22px; flex: none; }
.xv-wave i { width: 2.5px; border-radius: 2px; background: rgba(234, 245, 241, 0.55); transition: opacity 200ms; }
.xv-wave.is-live i { animation: xv-wave 900ms ease-in-out infinite; background: #9ad7bd; }
.xv-wave.is-live i:nth-child(2n) { animation-delay: 130ms; }
.xv-wave.is-live i:nth-child(3n) { animation-delay: 260ms; }
.xv-wave.is-live i:nth-child(5n) { animation-delay: 390ms; }
@keyframes xv-wave { 0%, 100% { transform: scaleY(0.35); } 50% { transform: scaleY(1); } }
.xv-track {
  position: relative; height: 4px; border-radius: 999px; overflow: hidden;
  background: rgba(255, 255, 255, 0.14); cursor: pointer;
}
.xv-fill {
  position: absolute; inset: 0 auto 0 0; border-radius: 999px;
  background: linear-gradient(90deg, #6fb99c, #c1e1d5);
  box-shadow: 0 0 12px rgba(111, 185, 156, 0.6);
  transition: width 120ms linear;
}
.xv-times { display: flex; justify-content: space-between; font-size: 11px; color: var(--xv-faint); font-variant-numeric: tabular-nums; }

/* ---- Responsive / a11y ---------------------------------------------- */
@media (max-height: 720px) {
  .xv-stage { gap: 10px; }
  .xv-caps { min-height: 66px; }
  .xv-cap-ai { font-size: 18px; -webkit-line-clamp: 3; }
  .xv-dock { padding-bottom: 22px; }
}
@media (max-width: 520px) {
  .xv-head { padding: 14px 16px; }
  .xv-cap-ai { font-size: 18px; }
  .xv-dock { gap: 14px; }
}
.xv-root :focus-visible { outline: 2px solid rgba(111, 185, 156, 0.85); outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) {
  .xv-root, .xv-root * { animation-duration: 1ms !important; animation-iteration-count: 1 !important; transition-duration: 1ms !important; }
}
`;

/** Inject the stylesheet once per document. */
export function useVoiceCallStyles(): void {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = VOICE_CALL_CSS;
    document.head.appendChild(el);
  }, []);
}
